import React, { useCallback, useEffect, useState } from "react";
import {
  Card,
  Input,
  Flex,
  Checkbox,
  Button,
  Typography,
  Form,
  message,
  Alert,
  LockOutlined,
  UserOutlined,
  MailOutlined,
  Divider,
  theme,
} from "@/shared/antd-imports";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { MagicLinkRequestForm } from "@/components/auth/MagicLinkRequestForm";
import { useTranslation } from "react-i18next";

import { useAppDispatch } from "@/hooks/useAppDispatch";
import { useAppSelector } from "@/hooks/useAppSelector";
import AuthPageHeader from "@/components/AuthPageHeader";
import { BrandAttribution } from "@/components/layout/BrandAttribution";
import { loginUser, setError } from "@/store/slices/authSlice";
import type { RootState } from "@/store";

interface LoginFormValues {
  email: string;
  password: string;
  remember?: boolean;
}

const LoginPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { token: { colorBgLayout } } = theme.useToken();
  const { isLoading, error, isAuthenticated } = useAppSelector(
    (state: RootState) => state.auth
  );
  const [form] = Form.useForm<LoginFormValues>();
  const [searchParams] = useSearchParams();
  // ANS-55: "Email me a sign-in link" (also opened from an expired link with ?mode=link)
  const [emailHint, setEmailHint] = useState("");
  const [mode, setMode] = useState<"password" | "link">(
    searchParams.get("mode") === "link" ? "link" : "password"
  );

  const validationRules = {
    email: [
      { required: true, message: t("login.emailRequired") },
      { type: "email" as const, message: t("login.emailInvalid") },
    ],
    password: [
      { required: true, message: t("login.passwordRequired") },
      { min: 8, message: t("login.passwordMin") },
    ],
  };

  // Redirect authenticated users to dashboard
  useEffect(() => {
    if (isAuthenticated) {
      navigate("/dashboard", { replace: true });
    }
  }, [navigate, isAuthenticated]);

  // Clear error when component unmounts
  useEffect(() => {
    return () => {
      dispatch(setError(null));
    };
  }, [dispatch]);

  const onFinish = useCallback(
    async (values: LoginFormValues) => {
      try {
        const result = await dispatch(
          loginUser({
            email: values.email,
            password: values.password,
          })
        );

        if (loginUser.fulfilled.match(result)) {
          message.success(t("login.success"));
          navigate("/dashboard", { replace: true });
        }
      } catch (error) {
        console.error("Login failed", error);
        message.error(t("login.failed"));
      }
    },
    [dispatch, navigate, t]
  );

  return (
    <div
      style={{
        minHeight: "100vh",
        width: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 16,
        background: colorBgLayout,
        padding: "24px 16px",
      }}
    >
      <Card
        style={{ width: 400, maxWidth: "100%" }}
        styles={{ body: { padding: "clamp(16px, 6vw, 32px)" } }}
      >
        <AuthPageHeader description={t("login.description")} />

        {mode === "link" ? (
          <MagicLinkRequestForm
            initialEmail={emailHint}
            onUsePassword={() => setMode("password")}
          />
        ) : (
        <>
        {error && (
          <Alert
            message={error}
            type="error"
            showIcon
            closable
            onClose={() => dispatch(setError(null))}
            style={{ marginBottom: 24 }}
          />
        )}

        <Form
          form={form}
          name="login"
          layout="vertical"
          autoComplete="off"
          requiredMark={false}
          initialValues={{ remember: true }}
          onFinish={onFinish}
        >
          <Form.Item 
            name="email" 
            rules={validationRules.email}
            label={t("login.emailLabel", "Email")}
          >
            <Input
              prefix={<UserOutlined />}
              placeholder={t("login.email")}
            />
          </Form.Item>

          <Form.Item 
            name="password" 
            rules={validationRules.password}
            label={t("login.passwordLabel", "Password")}
          >
            <Input.Password
              prefix={<LockOutlined />}
              placeholder={t("login.password")}
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: 16 }}>
            <Flex justify="space-between" align="center">
              <Form.Item name="remember" valuePropName="checked" noStyle>
                <Checkbox>{t("login.remember")}</Checkbox>
              </Form.Item>
              <Link to="/auth/forgot-password" className="ant-typography ant-typography-link">
                {t("login.forgot")}
              </Link>
            </Flex>
          </Form.Item>

          <Form.Item style={{ marginBottom: 16 }}>
            <Button
              block
              type="primary"
              htmlType="submit"
              loading={isLoading}
            >
              {t("login.signin")}
            </Button>
          </Form.Item>

          <Divider plain style={{ margin: "8px 0 16px" }}>
            {t("login.magic.or", { defaultValue: "or" })}
          </Divider>
          <Button
            block
            icon={<MailOutlined />}
            onClick={() => {
              dispatch(setError(null));
              setEmailHint(String(form.getFieldValue("email") ?? ""));
              setMode("link");
            }}
            style={{ marginBottom: 16 }}
          >
            {t("login.magic.send", { defaultValue: "Email me a sign-in link" })}
          </Button>

          <div style={{ textAlign: "center" }}>
            <Typography.Text type="secondary">
              {t("login.invite_only")}
            </Typography.Text>
          </div>
        </Form>
        </>
        )}
      </Card>
      <BrandAttribution />
    </div>
  );
};

export default LoginPage;
